import { inject, Injectable } from "@angular/core";
import { MatIconRegistry } from "@angular/material/icon";
import { DomSanitizer } from "@angular/platform-browser";
import { assetTypeToSvgMapping } from "src/app/helpers/utils/asset-to-svg-mapping.utils";
import { SymbolSvgUtils } from "src/app/helpers/utils/symbol-svg.utils";
import { defaultAssetIconStyle } from "src/app/shared/config/default-styles.config";
import { AssetType } from "src/app/shared/types/enums/map.enums";

@Injectable({ providedIn: "root" })
export class CustomIconLoaderService {
  private iconRegistry = inject(MatIconRegistry);
  private sanitizer = inject(DomSanitizer);
  private registeredIcons = new Set<string>();
  private readonly materialSymbolsIcons = ["delete"];

  initializeIcons(): void {
    this.injectMaterialSymbolsFont();

    this.iconRegistry.addSvgIcon(
      "querybuilder_icon_filled",
      this.sanitizer.bypassSecurityTrustResourceUrl("assets/icons/querybuilder_icon_filled.svg")
    );
    this.iconRegistry.addSvgIcon(
      "querybuilder_icon_outlined",
      this.sanitizer.bypassSecurityTrustResourceUrl("assets/icons/querybuilder_icon_outlined.svg")
    );
    this.registerAllAssetTypeIcons();
  }

  private injectMaterialSymbolsFont(): void {
    const id = "material-symbols-font";
    if (!document.getElementById(id)) {
      const iconsList = this.materialSymbolsIcons.join(",");
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href = `https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200&icon_names=${iconsList}`;
      document.head.appendChild(link);
    }

    if (!document.getElementById("material-symbols-style")) {
      const style = document.createElement("style");
      style.id = "material-symbols-style";
      style.innerHTML = `mat-icon.material-symbols-outlined {font-family: "Material Symbols Outlined";}`;
      document.head.appendChild(style);
    }
  }

  /**
   * Pre-register all asset type icons during application initialization
   */
  registerAllAssetTypeIcons(): void {
    // Get all asset types from the enum
    const assetTypes: AssetType[] = Object.values(AssetType).filter(
      assetType =>
        ![
          AssetType.UNKNOWN,
          AssetType.OH_PRIMARY_CONDUCTOR,
          AssetType.UG_PRIMARY_CONDUCTOR,
          AssetType.LOCATION_GROUP,
        ].includes(assetType)
    );

    assetTypes.forEach(assetType => {
      this.registerAssetTypeIcon(assetType);
    });
  }

  /**
   * Register a single asset type icon
   */
  private registerAssetTypeIcon(assetType: AssetType): void {
    const iconName = assetType + "Svg";

    // Skip if already registered
    if (this.registeredIcons.has(iconName)) {
      return;
    }

    const assetSvg = assetTypeToSvgMapping[assetType];
    if (!assetSvg) {
      console.warn(`No SVG mapping found for asset type: ${assetType}`);
      return;
    }

    let processedSvg = SymbolSvgUtils.addAssetIconSize(assetSvg, defaultAssetIconStyle);
    processedSvg = SymbolSvgUtils.addAssetIconStyleTag(processedSvg, defaultAssetIconStyle);

    this.iconRegistry.addSvgIconLiteral(iconName, this.sanitizer.bypassSecurityTrustHtml(processedSvg));

    this.registeredIcons.add(iconName);
  }
}
